// Encrypted, origin-private call recording storage. Plaintext exists only while
// MediaRecorder produces it or when the user explicitly exports a copy.
const DB_NAME = 'kaltiq-private-media';
const DB_VERSION = 1;
const STORE = 'recordings';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, {keyPath: 'id'});
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function transaction(db, mode, operation) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    try { result = operation(store); } catch (err) { reject(err); return; }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Recording storage transaction aborted'));
  });
}

export async function saveEncryptedRecording(blob, metadata = {}) {
  if (!window.crypto || !window.crypto.subtle || !window.indexedDB) {
    throw new Error('Encrypted recording storage is not supported by this browser.');
  }
  const id = 'rec_' + Date.now().toString(36) + '_' + crypto.getRandomValues(new Uint32Array(2)).join('');
  const key = await crypto.subtle.generateKey({name: 'AES-GCM', length: 256}, false, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const clear = await blob.arrayBuffer();
  const cipher = await crypto.subtle.encrypt({name: 'AES-GCM', iv}, key, clear);
  const record = {
    id, createdAt: new Date().toISOString(), durationSeconds: metadata.durationSeconds || 0,
    peer: metadata.peer || '', topic: metadata.topic || '', audioOnly: !!metadata.audioOnly,
    mime: blob.type || metadata.mime || 'video/webm', size: blob.size,
    iv, key, cipher
  };
  const db = await openDB();
  try { await transaction(db, 'readwrite', store => store.put(record)); } finally { db.close(); }
  return Object.assign({}, record, {key: undefined, cipher: undefined, iv: undefined});
}

export async function listRecordings() {
  const db = await openDB();
  try {
    const records = await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    return records.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))).map(record => ({
      id: record.id, createdAt: record.createdAt, durationSeconds: record.durationSeconds,
      peer: record.peer, topic: record.topic, audioOnly: record.audioOnly, mime: record.mime, size: record.size
    }));
  } finally { db.close(); }
}

export async function exportRecording(id) {
  const db = await openDB();
  let record;
  try {
    record = await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
  if (!record) throw new Error('Recording not found.');
  const clear = await crypto.subtle.decrypt({name: 'AES-GCM', iv: record.iv}, record.key, record.cipher);
  const extension = record.mime.includes('mp4') ? 'mp4' : (record.audioOnly ? 'webm' : 'webm');
  const url = URL.createObjectURL(new Blob([clear], {type: record.mime}));
  const link = document.createElement('a');
  link.href = url;
  link.download = `kaltiq-call-${record.createdAt.replace(/[:.]/g, '-')}.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function deleteRecording(id) {
  const db = await openDB();
  try { await transaction(db, 'readwrite', store => store.delete(id)); } finally { db.close(); }
}
